import { LOCAL_STORAGE_KEYS } from '@/constants/constants';
import { QUERY_KEYS } from '@/constants/query-keys';
import {
  getUserOnboardingData,
  UserOnboardingData,
} from '@/services/v3/onboarding';
import { isPublicSigningPath } from '@/utils/public-routes';
import { useEffect, useState } from 'react';
import { QueryKey, useQuery, UseQueryOptions } from 'react-query';

type QueryOptions = Omit<
  UseQueryOptions<UserOnboardingData, unknown, UserOnboardingData, QueryKey>,
  'queryKey' | 'queryFn'
>;

export const useGetUserOnboardingQuery = (options: QueryOptions = {}) => {
  const { enabled = true, ...restOptions } = options;
  const isOnPublicSigningPath =
    typeof window !== 'undefined' &&
    isPublicSigningPath(window.location.pathname);
  const shouldRunQuery = enabled && !isOnPublicSigningPath;

  // Track if session token is available (may be set after initial render)
  const [hasSessionToken, setHasSessionToken] = useState(
    () => !!(typeof localStorage !== 'undefined' && localStorage?.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN))
  );

  // Poll for session token availability
  useEffect(() => {
    if (hasSessionToken || !shouldRunQuery) return;

    const checkForToken = () => {
      if (typeof localStorage === 'undefined') return;

      const token = localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
      if (token) {
        setHasSessionToken(true);
      }
    };

    // Poll every 100ms for token
    const interval = setInterval(checkForToken, 100);

    // Stop polling after 10 seconds
    const timeout = setTimeout(() => clearInterval(interval), 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [hasSessionToken, shouldRunQuery]);

  return useQuery<UserOnboardingData>(
    [QUERY_KEYS.GET_USER_ONBOARDING],
    async () => {
      return getUserOnboardingData();
    },
    {
      staleTime: Infinity, // Don't refetch automatically - onboarding data rarely changes
      cacheTime: Infinity, // Keep in cache indefinitely
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      ...restOptions,
      // Only enable when both the caller wants it AND the token is available
      enabled: shouldRunQuery && hasSessionToken,
    }
  );
};

